'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import NewsletterSection from '@/components/NewsletterSection';
import Footer from '@/components/Footer';

// ─── Data ─────────────────────────────────────────────────────────────────────────────

interface NewsItem {
  id: number;
  slug: string;
  category: string;
  date: string;
  title: string;
  description: string;
  href: string;
  image: string;
  imageAlt: string;
}

const newsItems: NewsItem[] = [
{
  id: 1,
  slug: 'recruiting-head-crude-oil',
  category: 'Careers',
  date: 'March 2, 2026',
  title: 'UNOC IS RECRUITING: HEAD CRUDE OIL MARKETING AND SUPPLY',
  description: 'UNOC seeks a seasoned professional to lead crude oil marketing and supply operations as Uganda transitions to first oil production.',
  href: '/news/recruiting-head-crude-oil',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg',
  imageAlt: 'UNOC leadership team at 10th anniversary event Uganda petroleum sector milestone celebration'
},
{
  id: 2,
  slug: 'regional-energy-security',
  category: 'Press Release',
  date: 'March 2, 2026',
  title: 'UNOC Strengthens Regional Energy Security with Strategic Partnership',
  description: 'Strategic collaborations with regional partners to enhance energy infrastructure and security across East Africa.',
  href: '/news/regional-energy-security',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_182cf65df-1772643854633.png",
  imageAlt: 'UNOC and Baker Hughes EHO Ltd MoU signing ceremony for technical capabilities and Center of Excellence'
},
{
  id: 3,
  slug: '10-years-celebration',
  category: 'News',
  date: 'December 22, 2025',
  title: "UNOC Celebrated 10 Years Of Driving Uganda's Transformation In The Petroleum Sector",
  description: 'A decade of excellence in petroleum sector management, from inception to becoming a fully-fledged national oil company.',
  href: '/news/10-years-celebration',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM-2.jpeg',
  imageAlt: 'UNOC 10 year anniversary celebration with staff and government officials at petroleum sector event Uganda'
},
{
  id: 4,
  slug: 'baker-hughes-mou',
  category: 'Partnership',
  date: 'October 23, 2025',
  title: 'UNOC and Baker Hughes EHO Ltd Sign MoU to Build Technical Capacity',
  description: 'Partnership agreement to enhance technical capabilities and knowledge transfer in petroleum operations and management.',
  href: '/news/baker-hughes-mou',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/10/UNOC-BakerHughes.jpg',
  imageAlt: 'UNOC and Baker Hughes representatives at MoU signing for technical capacity building petroleum Uganda'
},
{
  id: 5,
  slug: 'eacop-milestone',
  category: 'Project Update',
  date: 'September 15, 2025',
  title: 'EACOP Project Reaches Major Construction Milestone',
  description: 'East African Crude Oil Pipeline construction progresses on schedule with significant infrastructure development achievements.',
  href: '/news/eacop-milestone',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8216a12-1778365791616.png",
  imageAlt: 'East African Crude Oil Pipeline EACOP construction milestone Uganda Tanzania midstream infrastructure'
},
{
  id: 6,
  slug: 'tilenga-progress',
  category: 'Project Update',
  date: 'August 8, 2025',
  title: 'Tilenga Project Drilling Activities Advance to Next Phase',
  description: 'TotalEnergies, in partnership with UNOC, progresses well-drilling operations at the Tilenga oil fields.',
  href: '/news/tilenga-progress',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16684f1ad-1770296082345.png",
  imageAlt: 'Tilenga oil project drilling activities at Lake Albert basin Buliisa District Uganda TotalEnergies UNOC'
}];


const categories = ['All', 'Careers', 'Press Release', 'News', 'Partnership', 'Project Update'];

// ─── Hero Section ─────────────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section className="bg-[#0F0F0F] pt-40 pb-20 relative overflow-hidden">
      {/* Dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
          backgroundSize: '40px 40px'
        }} />
      
      {/* Glow */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC0000] blur-[120px] rounded-full" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-gray-500 mb-8" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-[#CC0000] transition-colors">Home</Link>
          <span className="text-[#CC0000]">/</span>
          <span className="text-[#CC0000]">Media Center</span>
        </nav>

        {/* Label */}
        <div className="flex items-center gap-3 mb-6">
          <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
          <div className="h-[1px] w-12 bg-[#CC0000]" />
          <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Media Center</span>
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.05] tracking-tight max-w-4xl">
          Media &amp; Stories
        </h1>
        <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
          Stay up to date with the latest announcements, press releases, and project updates from UNOC.
        </p>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#CC0000] to-transparent opacity-50" />
    </section>);

}

// ─── Featured Image Section ─────────────────────────────────────────────────────────────────────────────

function FeaturedImageSection() {
  return (
    <section className="bg-white pt-16">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="relative overflow-hidden bg-[#0F0F0F]">
          <img
            src="https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg"
            alt="UNOC 10th anniversary celebration event with staff and government officials Uganda petroleum sector milestone"
            className="w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover" />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/85 via-[#0F0F0F]/30 to-transparent pointer-events-none" />
          {/* Corner brackets */}
          <div className="absolute top-6 left-6 w-8 h-8 border-l-2 border-t-2 border-[#CC0000]" />
          <div className="absolute bottom-6 right-6 w-8 h-8 border-r-2 border-b-2 border-[#CC0000]" />
          {/* Text overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">/</span>
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Newsroom</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold max-w-3xl leading-[1.1] tracking-tight">
              Latest announcements and project updates from UNOC
            </h2>
          </div>
        </div>
      </div>
    </section>);

}

// ─── News Archive Section ─────────────────────────────────────────────────────────────────────────────

function NewsArchiveSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredNews = newsItems.filter((item) => {
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    const matchesSearch =
    searchQuery === '' ||
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    cardRefs.current.forEach((ref, index) => {
      if (!ref) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards((prev) => new Set([...prev, index]));
            }, index * 80);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(ref);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [filteredNews.length]);

  return (
    <section className="py-24 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Section header */}
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
            <div className="h-[1px] w-12 bg-[#CC0000]" />
            <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Media Archive</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight">
            Latest <span className="text-[#CC0000]">Updates</span>
          </h2>
        </div>

        {/* Search & Filter */}
        <div className="bg-white p-8 border border-gray-200 mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true">
                
                <path d="m21 21-4.34-4.34" />
                <circle cx="11" cy="11" r="8" />
              </svg>
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-sm focus:outline-none focus:border-[#CC0000] bg-[#F8F9FA] text-sm transition-colors" />
              
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mr-3">Category:</span>
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-sm text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
              activeCategory === cat ?
              'bg-[#0F0F0F] text-white' :
              'bg-[#F8F9FA] text-gray-600 hover:bg-gray-200 border border-gray-200'}`
              }>
              
                {cat}
              </button>
            )}
          </div>
        </div>

        {/* News grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-gray-200">
          {filteredNews.map((item, index) =>
          <div
            key={item.id}
            ref={(el) => {cardRefs.current[index] = el;}}
            style={{
              opacity: visibleCards.has(index) ? 1 : 0,
              transform: visibleCards.has(index) ? 'none' : 'translateY(20px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease'
            }}>
            
              <Link
              href={item.href}
              className="block bg-white border-r border-b border-gray-200 hover:bg-[#0F0F0F] transition-colors duration-300 group h-full flex flex-col">
              
                {/* Image */}
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                
                  <div className="absolute inset-0 bg-[#0F0F0F]/0 group-hover:bg-[#0F0F0F]/30 transition-colors duration-300" />
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col flex-1">
                  {/* Category & Date */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-[#CC0000]">
                      {item.category}
                    </span>
                    <span className="font-mono text-[10px] text-gray-400 group-hover:text-gray-500 uppercase tracking-wider transition-colors">
                      {item.date}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-extrabold text-[#0F0F0F] group-hover:text-white mb-4 transition-colors leading-snug">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 group-hover:text-gray-400 text-sm mb-8 line-clamp-3 flex-1 transition-colors leading-relaxed">
                    {item.description}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 group-hover:border-white/10 transition-colors">
                    <span className="text-xs font-bold text-[#0F0F0F] group-hover:text-[#CC0000] uppercase tracking-[0.15em] transition-colors">
                      Read more
                    </span>
                    <span className="w-8 h-8 bg-[#CC0000] flex items-center justify-center rounded-sm group-hover:rotate-45 transition-transform duration-300">
                      <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-white"
                      aria-hidden="true">
                      
                        <path d="M7 7h10v10" />
                        <path d="M7 17 17 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {filteredNews.length === 0 &&
          <div className="col-span-3 border-r border-b border-gray-200 p-16 text-center">
              <p className="text-gray-500 text-sm">No articles found matching your search.</p>
            </div>
          }
        </div>
      </div>
    </section>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────────────────────

export default function NewsPage() {
  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <Navbar />

      <main id="main-content" className="flex-1 mt-[120px]">
        <HeroSection />
        <FeaturedImageSection />
        <NewsArchiveSection />
      </main>

      <NewsletterSection />
      <Footer />
    </div>);

}